import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import { normalizeWidgetTheme, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getWidgetDataURI, apiGet, toggleWidgetTheme, APIURL, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from '../../helpers';

export class TraderverseRelativeValuationBarChart {
  private container: HTMLElement;
  private config: WidgetConfig;
  private shadowRoot: ShadowRoot;
  private chartInstance: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  

  private apiData: any[] = [];
  private apiLoaded: boolean = false;
  private filters: Record<string, any> = {
    p_benchmark_type: 'market_average',
    p_custom_benchmark: 1500.25,
    p_display_mode: 'relative',
    p_sort_order: 'highest_to_lowest',
    p_date_selection: '2025-06-19',
    p_exchange_filter: 'NASDAQ',
    p_min_pe: 5,
    p_max_pe: 30
  };

  constructor(config: WidgetConfig) {
    this.config = { theme: 'default-dark', ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });

    this.fetchApiData();

    this.widgetResizeObserver = new ResizeObserver(() => {
      if (this.chartInstance) this.chartInstance.resize();
    });
  }

  private async fetchApiData(): Promise<void> {
    try {
      const params = new URLSearchParams();
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      const url = `${APIURL}/v1/discover/relative-valuation-sector?${params.toString()}`;
      const response = await apiGet({
        method: 'GET',
        url,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const json = await response.json();
      if (json && json.data && Array.isArray(json.data.data)) {
        this.apiData = json.data.data;
        this.apiLoaded = true;
        this.updateDisplay();
      }
    } catch (err) {
      console.error('Failed to fetch API data:', err);
    }
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    setupHeaderPin(this.shadowRoot, this.config);
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) {
      widget.classList.add('loading');
      this.widgetResizeObserver?.observe(widget);
    }

    await this.injectChartLibrary();
    await this.attachStyles();

    if (widget) {
      widget.classList.remove('loading');
    }

    this.updateDisplay();
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    this.shadowRoot.innerHTML = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Relative Valuation Bar Chart
          </h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            <div class="item"></div>
            <span></span>
            <div class="filtersBox sideBar hidden"></div>
          </div>

          <div class="chartDv" id="relativeValuationBarChartDv" style="height:320px;"></div>
        </div>
      </div>`;

    const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filterBtn && filtersBox) {
      filterBtn.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });

      filtersBox.innerHTML = `
        <form class="filters-form" style="display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end;">
          <div>
            <label>Benchmark Type</label>
            <select name="p_benchmark_type">
              <option value="market_average">Market Average</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label>Custom Benchmark</label>
            <input type="number" name="p_custom_benchmark" step="0.01" placeholder="e.g. 1500.25" />
          </div>
          <div>
            <label>Display Mode</label>
            <select name="p_display_mode">
              <option value="absolute">Absolute</option>
              <option value="relative">Relative</option>
            </select>
          </div>
          <div>
            <label>Sort Order</label>
            <select name="p_sort_order">
              <option value="highest_to_lowest">Highest to Lowest</option>
              <option value="lowest_to_highest">Lowest to Highest</option>
            </select>
          </div>
          <div>
            <label>Date</label>
            <input type="date" name="p_date_selection" />
          </div>
          <div>
            <label>Exchange Filter</label>
            <input type="text" name="p_exchange_filter" placeholder="e.g. NASDAQ" />
          </div>
          <div>
            <label>Min PE</label>
            <input type="number" name="p_min_pe" step="1" placeholder="5" />
          </div>
          <div>
            <label>Max PE</label>
            <input type="number" name="p_max_pe" step="1" placeholder="30" />
          </div>
        </form>
      `;

      const form = filtersBox.querySelector('.filters-form') as HTMLFormElement;
      if (form) {
        (form.elements.namedItem('p_benchmark_type') as HTMLSelectElement).value = this.filters.p_benchmark_type;
        (form.elements.namedItem('p_custom_benchmark') as HTMLInputElement).value = this.filters.p_custom_benchmark;
        (form.elements.namedItem('p_display_mode') as HTMLSelectElement).value = this.filters.p_display_mode;
        (form.elements.namedItem('p_sort_order') as HTMLSelectElement).value = this.filters.p_sort_order;
        (form.elements.namedItem('p_date_selection') as HTMLInputElement).value = this.filters.p_date_selection;
        (form.elements.namedItem('p_exchange_filter') as HTMLInputElement).value = this.filters.p_exchange_filter;
        (form.elements.namedItem('p_min_pe') as HTMLInputElement).value = this.filters.p_min_pe;
        (form.elements.namedItem('p_max_pe') as HTMLInputElement).value = this.filters.p_max_pe;
      }

      const benchmarkType = filtersBox.querySelector('select[name="p_benchmark_type"]') as HTMLSelectElement;
      const customBenchmark = filtersBox.querySelector('input[name="p_custom_benchmark"]') as HTMLInputElement;
      if (benchmarkType && customBenchmark) {
        customBenchmark.disabled = benchmarkType.value !== 'custom';
        benchmarkType.addEventListener('change', () => {
          customBenchmark.disabled = benchmarkType.value !== 'custom';
          if (customBenchmark.disabled) {
            customBenchmark.value = '';
          } else {
            customBenchmark.value = this.filters.p_custom_benchmark;
          }
        });
      }

      if (form) {
        form.addEventListener('change', () => {
          this.filters.p_benchmark_type = (form.elements.namedItem('p_benchmark_type') as HTMLSelectElement).value;
          this.filters.p_custom_benchmark = (form.elements.namedItem('p_custom_benchmark') as HTMLInputElement).value || '';
          this.filters.p_display_mode = (form.elements.namedItem('p_display_mode') as HTMLSelectElement).value;
          this.filters.p_sort_order = (form.elements.namedItem('p_sort_order') as HTMLSelectElement).value;
          this.filters.p_date_selection = (form.elements.namedItem('p_date_selection') as HTMLInputElement).value;
          this.filters.p_exchange_filter = (form.elements.namedItem('p_exchange_filter') as HTMLInputElement).value;
          this.filters.p_min_pe = (form.elements.namedItem('p_min_pe') as HTMLInputElement).value;
          this.filters.p_max_pe = (form.elements.namedItem('p_max_pe') as HTMLInputElement).value;
          if (this.filters.p_benchmark_type !== 'custom') {
            this.filters.p_custom_benchmark = '';
          }
          this.apiLoaded = false;
          this.fetchApiData();
        });
      }
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);

      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private updateDisplay(): void {
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (!this.apiLoaded) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderChart();
  }

  private renderChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#relativeValuationBarChartDv') as HTMLElement;
    if (!container) return;

    const existing = echarts.getInstanceByDom(container);
    if (existing) echarts.dispose(existing);

    if (!this.apiData?.length) {
      container.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      this.chartInstance = null;
      return;
    }

    container.setAttribute('data-testing-id', 'relative-valuation-bar-chart');
    this.chartInstance = echarts.init(container);

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');

    const yAxisData = this.apiData.map((item: any) => item.sector);
    const positiveData = this.apiData.map((item: any) => item.relative_value > 0 ? item.relative_value : 0);
    const negativeData = this.apiData.map((item: any) => item.relative_value < 0 ? item.relative_value : 0);

    // Reserve enough left space so long sector names (e.g. "Consumer Discretionary") are not clipped
    const longestLabelChars = yAxisData.reduce((max: number, label: string) => {
      return Math.max(max, String(label || '').length);
    }, 0);
    const gridLeft = Math.min(Math.max(Math.ceil(longestLabelChars * 7.5) + 16, 100), 200);

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...getEChartsTooltipTheme(widgetBox),
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          const index = items[0]?.dataIndex ?? 0;
          const row = this.apiData[index];
          if (!row) return '';
          const value = Number(row.relative_value ?? 0);
          const color = value >= 0 ? '#3CF26E' : '#FF4A4A';
          const pe = row.pe != null ? Number(row.pe).toFixed(2) : '—';
          return `
            <div style="min-width:140px">
              <div style="font-weight:600;margin-bottom:6px">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>
                ${row.sector || '—'}
              </div>
              <div>Relative Value: <b>${value.toFixed(2)}</b></div>
              <div>PE: <b>${pe}</b></div>
            </div>
          `;
        }
      },
      grid: { left: gridLeft, right: 16, top: 8, bottom: 28, containLabel: false },
      xAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: 'dotted', color: splitLineColor }},
        axisLabel: { color: textColor }
      },
      yAxis: {
        type: 'category',
        data: yAxisData,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        axisLabel: {
          color: textColor,
          fontSize: 12,
          interval: 0,
          margin: 8
        }
      },
      series: [
        {
          name: 'positive',
          type: 'bar',
          data: positiveData,
          barWidth: '60%',
          itemStyle: {
            color: '#3CF26E',
            borderRadius: [0, 6, 6, 0]
          }
        },
        {
          name: 'negative',
          type: 'bar',
          data: negativeData,
          barWidth: '60%',
          itemStyle: {
            color: '#FF4A4A',
            borderRadius: [6, 0, 0, 6]
          }
        }
      ]
    };

    this.chartInstance.setOption(option);
  }

    private handleResize(): void {
    if (this.apiData && Array.isArray(this.apiData)) {
      try {
        this.apiData.forEach(chart => {
          if (chart) chart.resize();
        });
      } catch (err) {
        console.error('Error resizing chart:', err);
      }
    }
  }


  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
      if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
      this.eventHandlers.get(type)!.push(handler);
    }
  
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
      const list = this.eventHandlers.get(type);
      if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
  
    public async getWidgetImage(): Promise<string> {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      return await getWidgetDataURI(widget);
    }
  
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }

  public destroy(): void {
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    
    if (this.apiData && Array.isArray(this.apiData)) {
      this.apiData.forEach(chart => {
        if (chart) chart.dispose();
      });
      this.apiData = [];
    }
    
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
      const handlers = this.eventHandlers.get(type);
      if (handlers) {
        const evt: WidgetEvent = { type, data };
        handlers.forEach(h => h(evt));
      }
    }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/>
            <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/>
          </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseRelativeValuationBarChart: typeof TraderverseRelativeValuationBarChart;
  }
}
if (typeof window !== 'undefined') {
  window.TraderverseRelativeValuationBarChart = TraderverseRelativeValuationBarChart;
}